import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Inbox, Search } from "lucide-react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "components/ui/input-group";
import Avatar from "components/shared/Avatar";
import Button from "components/shared/Button";
import Checkbox from "components/shared/inputs/Checkbox";
import Spinner from "components/shared/Spinner";
import useApi from "utils/api";
import { applyTimezone } from "utils/helpers/date";
import { getPrimaryActiveResource, renderUserName } from "utils/helpers/user";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { cn } from "lib/utils";
import type { CalculatedSection } from "../../../../Component/components/drawers/ShiftTrade/types";

const PER_PAGE = 20;

interface UserSelectProps {
    sections: CalculatedSection[];
    shiftId?: string;
    value: any[];
    onChange: (users: any[]) => void;
    error?: string | null;
}

function UserSelect({ sections, shiftId, value, onChange, error }: UserSelectProps) {
    const { t } = useTranslation();
    const { authGet } = useApi();
    const [inputValue, setInputValue] = useState("");
    const [search, setSearch] = useState("");

    const firstSection = sections[0];
    const lastSection = sections[sections.length - 1];

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ["available-users", "direct-offer", shiftId, search, firstSection?.start, lastSection?.end],
        queryFn: async ({ pageParam }) => {
            const timezone = firstSection?.location?.timezoneValue;
            const response = await authGet("/schedules/v2/direct-offer/users", {
                params: {
                    search,
                    shiftId,
                    job: firstSection?.job?.jobId,
                    location: firstSection?.location?.locationId,
                    groupId: firstSection?.group,
                    startTime: applyTimezone(moment.parseZone(firstSection?.start), timezone, true).toISOString(true),
                    endTime: applyTimezone(moment.parseZone(lastSection?.end), timezone, true).toISOString(true),
                    page: pageParam,
                    perPage: PER_PAGE,
                },
            });

            return {
                items: (Array.isArray(response?.data) ? response.data : response?.data?.users || []) as any[],
                total: (response?.meta?.total || 0) as number,
            };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce((total, page) => total + page.items.length, 0);
            return loaded < lastPage.total ? allPages.length : undefined;
        },
        enabled: !!firstSection,
        refetchOnWindowFocus: false,
    });

    const users = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

    const applySearch = useMemo(() => debounce((next: string) => {
        setSearch(next);
        onChange([]);
    }, 600), [onChange]);

    useEffect(() => () => applySearch.cancel(), [applySearch]);

    const isSelected = (user: any) => value.some((selected) => selected.id === user.id);

    const toggle = (user: any) => onChange(
        isSelected(user) ? value.filter((selected) => selected.id !== user.id) : [...value, user]
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-4">
            <InputGroup>
                <InputGroupAddon>
                    <Search />
                </InputGroupAddon>
                <InputGroupInput
                    value={inputValue}
                    placeholder={`${t("search")}...`}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        applySearch(e.target.value);
                    }}
                />
            </InputGroup>

            {users.length > 0 && (
                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                    <span className="tw-text-gray-500">
                        {value.length}/{data?.pages[0]?.total ?? 0}
                    </span>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button type="button" className="tw-text-primary tw-font-semibold" onClick={() => onChange(users)}>
                            {t("select-all")}
                        </button>
                        {" | "}
                        <button type="button" className="tw-text-primary tw-font-semibold" onClick={() => onChange([])}>
                            {t("select-none")}
                        </button>
                    </div>
                </div>
            )}

            {error && <p className="tw-m-0 tw-text-sm tw-text-rose-500">{t(error)}</p>}

            {users.length === 0 ? (
                isFetching ? (
                    <div className="tw-flex tw-justify-center tw-py-5">
                        <Spinner />
                    </div>
                ) : (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-5 tw-text-neutral-400 tw-border tw-border-dashed tw-rounded-lg">
                        <Inbox />
                        <span className="tw-text-sm">{t("no-data")}</span>
                    </div>
                )
            ) : (
                <div className="tw-max-h-80 tw-overflow-y-auto tw-border tw-rounded-lg tw-divide-y">
                    {users.map((user) => {
                        const job = getPrimaryActiveResource(user.jobs || []);
                        const location = getPrimaryActiveResource(user.locations || []);
                        const resourceLabel = [job, location]
                            .filter(Boolean)
                            .map(renderSettingsResourceLabel)
                            .join(" · ");

                        return (
                            <label
                                key={user.id}
                                className={cn(
                                    "tw-flex tw-items-center tw-gap-3 tw-m-0 tw-px-4 tw-py-3 tw-text-sm tw-cursor-pointer hover:tw-bg-gray-50",
                                    isSelected(user) && "tw-bg-primary/5"
                                )}
                            >
                                <Checkbox checked={isSelected(user)} onCheckedChange={() => toggle(user)} />
                                <Avatar user={user} size="xs" textStyles="text-xs" />
                                <span className="tw-font-medium">{renderUserName(user)}</span>
                                {user.employeeNumber && (
                                    <span className="tw-text-gray-500">#{user.employeeNumber}</span>
                                )}
                                <span className="tw-flex-1 tw-min-w-0 tw-truncate tw-text-right tw-text-gray-600">
                                    {resourceLabel}
                                </span>
                            </label>
                        );
                    })}

                    {hasNextPage && (
                        <div className="tw-flex tw-justify-center tw-py-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => fetchNextPage()}
                                loading={isFetchingNextPage}
                                disabled={isFetchingNextPage}
                            >
                                {t("load-more")}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default UserSelect;
